import { getApiBaseUrl } from "src/config/endpoints";
import { normalizeAnalysisDetails } from "src/layouts/SocialListingAgent/reportUtils";

const API_BASE_URL = getApiBaseUrl();

/** One brand row — company_name required (competitive `companies[]` or flat brand-analysis body). */
export interface SocialListeningCollectCompany {
  company_name: string;
  facebook_handle?: string;
  instagram_handle?: string;
  tiktok_brand?: string;
  facebook_group_name?: string;
  reddit_analysis?: string;
  reddit_country?: string;
  reddit_category?: string;
  reddit_additional_subreddits?: string[];
  posts_per_platform?: number;
}

export interface SocialListeningCollectPayload {
  user_id: number;
  tenant_id: number;
  companies: SocialListeningCollectCompany[];
}

export interface SocialListeningCollectResponse {
  analysis_ids: number[];
}

export type SocialListeningBrandAnalysisPayload = {
  user_id: number;
  tenant_id: number;
} & SocialListeningCollectCompany;

export interface SocialListeningBrandAnalysisResponse {
  analysis_id: number;
}

const SOCIAL_LISTENING_COMPETITIVE_ANALYSIS_ENDPOINT = `${API_BASE_URL}/social-listening/competitive-analysis`;
const SOCIAL_LISTENING_BRAND_ANALYSIS_ENDPOINT = `${API_BASE_URL}/social-listening/brand-analysis`;

const getApiKey = () => {
  const apiKey = localStorage.getItem("api_key");
  if (!apiKey) {throw new Error("Datatram API key missing from localStorage");}
  return apiKey;
};

const parseAnalysisIds = (data: unknown): number[] => {
  if (!data || typeof data !== "object") {return [];}
  const record = data as Record<string, unknown>;
  let raw: unknown[] | undefined;
  if (Array.isArray(record.analysis_ids)) {
    raw = record.analysis_ids;
  } else if (record.data && typeof record.data === "object") {
    const nested = (record.data as Record<string, unknown>).analysis_ids;
    if (Array.isArray(nested)) {raw = nested;}
  }
  if (!raw) {return [];}
  return raw.map(id => Number(id)).filter(id => !Number.isNaN(id));
};

const parseAnalysisId = (data: unknown): number => {
  if (!data || typeof data !== "object") {return 0;}
  const record = data as Record<string, unknown>;
  let raw: unknown = record.analysis_id;
  if (raw === undefined && record.data && typeof record.data === "object") {
    raw = (record.data as Record<string, unknown>).analysis_id;
  }
  const n = Number(raw);
  return Number.isNaN(n) ? 0 : n;
};

export const startSocialListeningCompetitiveAnalysis = async (
  payload: SocialListeningCollectPayload,
): Promise<SocialListeningCollectResponse> => {
  const apiKey = getApiKey();
  const url = `${SOCIAL_LISTENING_COMPETITIVE_ANALYSIS_ENDPOINT}?api_key=${apiKey}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Social listening request failed: ${response.status} ${response.statusText} ${errorText}`,
    );
  }

  const data = await response.json().catch(() => ({}));
  return { analysis_ids: parseAnalysisIds(data) };
};

export const startSocialListeningBrandAnalysis = async (
  payload: SocialListeningBrandAnalysisPayload,
): Promise<SocialListeningBrandAnalysisResponse> => {
  const apiKey = getApiKey();
  const url = `${SOCIAL_LISTENING_BRAND_ANALYSIS_ENDPOINT}?api_key=${apiKey}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Social listening request failed: ${response.status} ${response.statusText} ${errorText}`,
    );
  }

  const data = await response.json().catch(() => ({}));
  return { analysis_id: parseAnalysisId(data) };
};

export interface SocialListeningHistoryItem {
  id?: string | number;
  analysis_id?: number;
  company_name?: string;
  report_title?: string;
  author_name?: string;
  author?: string;
  created_by_name?: string;
  created_by?: string;
  user_name?: string;
  username?: string;
  full_name?: string;
  name?: string;
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  status?: string;
  analysis_status?: string;
  state?: string;
  processing_status?: string;
  is_processing?: boolean;
  processing?: boolean;
  is_completed?: boolean;
  analysis?: {
    status?: string;
  };
  analysis_type?: "brand_analysis" | "competitive_analysis";
}

export const fetchSocialListeningHistory = async (
  userId: number,
): Promise<SocialListeningHistoryItem[]> => {
  const apiKey = getApiKey();
  const url = `${API_BASE_URL}/social-listening/analyses-list?api_key=${apiKey}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ user_id: userId }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Social listening history failed: ${response.status} ${response.statusText} ${errorText}`,
    );
  }

  const data = await response.json().catch(() => ({}));
  if (Array.isArray(data)) {return data;}
  if (Array.isArray(data?.analyses)) {return data.analyses;}
  if (Array.isArray(data?.data)) {return data.data;}
  if (Array.isArray(data?.results)) {return data.results;}
  return [];
};

export interface SocialListeningHighlightLink {
  label: string;
  url?: string;
  tooltip?: string;
  preview_image?: string;
}

export type SocialListeningHighlight =
  | string
  | {
      text: string;
      links?: SocialListeningHighlightLink[];
    };

export interface SocialListeningSourcePost {
  post_id?: string;
  post_details: string;
  post_url?: string;
  type?: string;
  recent_comments?: string[];
  /** Present when analysis-details API includes engagement; omitted until backend ships it */
  likes_count?: number;
  post_likes?: number;
  engagement?: {
    likes?: number;
    comments?: number;
    shares?: number;
  };
}

export interface SocialListeningSourceBreakdown {
  source_name: string;
  total_posts: number;
  total_comments: number;
  sentiment_data: {
    positive_count: number;
    positive_percentage: number;
    neutral_count: number;
    neutral_percentage: number;
    negative_count: number;
    negative_percentage: number;
    total_count: number;
  };
  key_themes: string[];
  top_positive_points: string[];
  top_negative_points: string[];
  /** Populated when analysis-details API includes per-post rows */
  posts?: SocialListeningSourcePost[];
}

export interface SocialListeningAnalysisDetails {
  analysis_id: number;
  analysis_type?: string;
  company_name: string;
  status: string;
  sentiment_method: string;
  translation_method: string;
  data_quality_score: number;
  sources_analyzed: string[];
  overall_sentiment: {
    total_texts: number;
    total_posts?: number;
    total_count?: number;
    positive_count: number;
    positive_percentage: number;
    neutral_count: number;
    neutral_percentage: number;
    negative_count: number;
    negative_percentage: number;
  };
  data_sufficiency_warnings?: string[];
  sample_post_excerpts?: unknown[];
  sample_comment_excerpts?: unknown[];
  report?: Record<string, unknown>;
  narrative_summary: string;
  /** Future API field; UI falls back to narrative_summary */
  executive_summary?: string;
  key_insights: string[];
  /** Future API field; UI falls back to key_insights */
  highlights?: SocialListeningHighlight[];
  recommended_actions: string[];
  source_breakdown: SocialListeningSourceBreakdown[];
  analysis_date: string;
  completed_at: string;
  created_at: string;
  llm_comparison?: Record<string, string | string[]>;
}

export const fetchSocialListeningAnalysisDetails = async (
  userId: number,
  analysisId: number,
): Promise<SocialListeningAnalysisDetails | null> => {
  const apiKey = getApiKey();
  const response = await fetch(
    `${API_BASE_URL}/social-listening/analysis-details?api_key=${apiKey}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({ user_id: userId, analysis_id: analysisId }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Social listening details failed: ${response.status} ${response.statusText} ${errorText}`,
    );
  }

  const json = await response.json().catch(() => ({}));
  return normalizeAnalysisDetails(json);
};

export const deleteSocialListeningAnalysis = async (
  userId: number,
  analysisId: number,
): Promise<void> => {
  const apiKey = getApiKey();
  const response = await fetch(
    `${API_BASE_URL}/social-listening/delete-analysis?api_key=${apiKey}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user_id: userId, analysis_id: analysisId }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    throw new Error(
      `Social listening delete failed: ${response.status} ${response.statusText} ${errorText}`,
    );
  }
};
